// Vendors
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Modal from '@material-ui/core/Modal';

import { PrivacyPolicyUpdateAbout } from './About';
import { validateCookie, createCookie } from './utils/cookie';
import { withNamespaces } from 'react-i18next';

const styles = (theme) => ({
  '@keyframes slideIn': {
    '0%': {
      transform: 'translateX(-200%)',
    },
    '100%': {
      transform: 'translateX(0)',
    },
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: '0',
    },
    '100%': {
      opacity: '1',
    },
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 100000,
    pointerEvents: 'none',
  },
  notification: {
    pointerEvents: 'all',
    position: 'absolute',
    bottom: 24,
    background: 'rgba(31, 31, 31, 0.95)',
    color: '#fff',
    display: 'flex',
    justifyContent: 'flex-end',
    borderRadius: '100em 0 0 100em',
    opacity: 1,
    right: 0,
    left: 20,
    transition: 'opacity 0.6s ease-in-out',
    animation: '$fadeIn .6s ease-in-out',

    [theme.customBreakpoints.up('sm1')]: {
      borderRadius: '100em',
      animation: '$slideIn .7s ease-in-out',
      transition: 'transform.7s ease-in-out',
      left: 'auto',
      width: 'calc(100% + 80px)',
      justifyContent: 'center',
      right: '-95px',
    },
  },

  hideNotification: {
    display: 'none',
  },

  triggerExitAnimation: {
    opacity: 0,
    [theme.customBreakpoints.up('sm1')]: {
      transform: 'translateX(200%)',
      opacity: 1,
    },
  },

  notificationInner: {
    width: '82%',
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    marginTop: '10px',

    [theme.customBreakpoints.up('sm1')]: {
      flexDirection: 'row',
      width: '100%',
      paddingRight: 36,
      margin: '0 100px',
      justifyContent: 'space-between',
      position: 'relative',
      alignItems: 'center',
      opacity: 0,
      animation: '$fadeIn .7s ease-in-out',
      animationDelay: '.8s',
      animationFillMode: 'forwards',
    },

    [theme.customBreakpoints.up('md1')]: {
      paddingLeft: 40,
      margin: '0 100px',
      paddingTop: 28,
      paddingBottom: 28,
    },
  },

  header: {
    fontFamily: 'Pluto Sans Bold',
    fontSize: 22,
    lineHeight: '32px',

    [theme.customBreakpoints.up('sm1')]: {
      marginTop: 0,
    },
    [theme.customBreakpoints.up('md1')]: {
      fontSize: 24,
      marginBottom: 8,
    },
    [theme.customBreakpoints.up('lg1')]: {
      fontSize: 28,
      lineHeight: '32px',
    },
  },

  subheader: {
    fontSize: 18,
    lineHeight: '26px',
    fontFamily: 'Pluto Sans Light',

    [theme.customBreakpoints.up('sm1')]: {
      marginTop: 0,
    },

    [theme.customBreakpoints.up('md1')]: {
      fontSize: 20,
      marginBottom: 0,
    },

    [theme.customBreakpoints.up('lg1')]: {
      fontSize: 24,
      lineHeight: '32px',
    },
  },

  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    [theme.customBreakpoints.up('sm1')]: {
      flexDirection: 'row-reverse',
    },
  },
  buttonLabel: {
    fontSize: 18,
  },
  buttonRoot: {
    minHeight: 48,
    padding: [[8, 8]],
    borderRadius: 50,
    marginTop: 10,

    [theme.customBreakpoints.up('sm1')]: {
      marginTop: 0,
    },
  },
  buttonAccept: {
    backgroundColor: '#ffde2b',
    width: 150,
  },
  buttonAbout: {
    color: '#A8A8A8',
    width: 150,
    textDecoration: 'none',
  },

  errorOutlineIcon: {
    position: 'absolute',
    left: -55,
    fill: '#ffde2b',
    fontSize: 48,
    display: 'none',
    [theme.customBreakpoints.up('sm1')]: {
      display: 'block',
    },
  },
  libraryBooksIcon: {
    fill: '#A8A8A8',
    marginRight: 10,
    fontSize: 22,
  },
  checkIcon: {
    marginRight: 10,
    fontSize: 22,
  },
});

export class _PrivacyPolicyUpdateNotificationComponent extends PureComponent {
  static propTypes = {
    countryCode: PropTypes.string,
    classes: PropTypes.shape({}).isRequired,
    isEmbed: PropTypes.bool.isRequired,
    policyAccepted: PropTypes.func.isRequired,
    policyViewed: PropTypes.func.isRequired,
    policyViewedAccepted: PropTypes.func.isRequired,
    marketingRegion: PropTypes.string,
  };
  static defaultProps = {
    countryCode: 'na',
  };

  static shouldHideNotification(countryCode) {
    if (countryCode !== 'us') {
      return true;
    }

    const privacyPolicyLastUpdated = Date.UTC(2020, 0, 1, 0, 0, 0, 0);

    return validateCookie(privacyPolicyLastUpdated);
  }

  state = {
    showAbout: false,
    triggerExitAnimation: false,
  };

  componentDidMount = () => {
    const hideNotification = _PrivacyPolicyUpdateNotificationComponent.shouldHideNotification(
      this.props.countryCode,
    );
    this.props.updatePrivacyPolicy(hideNotification);
  };

  showAbout = () => {
    this.setState({ showAbout: true }, () => {
      this.props.policyViewed();
    });
  };

  accept = async (isAbout = false) => {
    let hideNotification = null;

    // hide popup for embed player without cookie functionality
    if (this.props.isEmbed) {
      hideNotification = true;
    } else {
      const response = await createCookie(this.props.isEmbed);

      hideNotification = _PrivacyPolicyUpdateNotificationComponent.shouldHideNotification(
        this.props.countryCode,
      );
      if (response.continueWithoutVerify) {
        hideNotification = true;
      }
    }

    // hideNotification value is set
    if (hideNotification !== null) {
      this.setState({ showAbout: false }, () => {
        if (isAbout) {
          this.props.policyViewedAccepted();
          this.props.updatePrivacyPolicy(hideNotification);
        } else {
          this.props.policyAccepted();
          this.setState({ triggerExitAnimation: true });
          setTimeout(() => this.props.updatePrivacyPolicy(hideNotification), 800);
        }
      });
    }
  };

  notificationAccepted = () => {
    this.accept(false);
  };

  aboutAccepted = () => {
    this.accept(true);
  };

  render() {
    const { classes, t, countryCode, marketingRegion } = this.props;

    if (this.props.hidePrivacyNotification) {
      return null;
    }

    return (
      <Modal open style={{ zIndex: 1500 }} BackdropProps={{ invisible: true }}>
        <Fragment>
          <PrivacyPolicyUpdateAbout
            countryCode={countryCode}
            show={this.state.showAbout}
            accept={this.aboutAccepted}
            marketingRegion={marketingRegion}
          />

          <div className={classes.overlay} />

          <section
            className={classnames(classes.notification, {
              [classes.hideNotification]: this.state.showAbout,
              [classes.triggerExitAnimation]: this.state.triggerExitAnimation,
            })}
          >
            <div className={classes.notificationInner}>
              <ErrorOutlineIcon className={classes.errorOutlineIcon} />
              <div className={classes.textContainer}>
                <h1 className={classes.header}>
                  {t(
                    'drop_in',
                    'Drop in for 100s of free TV channels and 1000s of movies & TV shows.',
                  )}
                </h1>
                <h2 className={classes.subheader}>
                  {t(
                    'by_clicking_accept',
                    'By clicking “Accept,” you agree to our Terms of Use and Privacy Policy.',
                  )}
                </h2>
              </div>

              <div className={classes.buttonsContainer}>
                <Button
                  className={classes.buttonAccept}
                  classes={{ label: classes.buttonLabel, root: classes.buttonRoot }}
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={this.notificationAccepted}
                >
                  <CheckIcon className={classes.checkIcon} />
                  {t('accept', 'Accept')}
                </Button>

                <Button
                  className={classes.buttonAbout}
                  classes={{ label: classes.buttonLabel, root: classes.buttonRoot }}
                  size="large"
                  onClick={this.showAbout}
                >
                  <LibraryBooksIcon className={classes.libraryBooksIcon} />
                  {t('read', 'Read')}
                </Button>
              </div>
            </div>
          </section>
        </Fragment>
      </Modal>
    );
  }
}

export const PrivacyPolicyUpdateNotificationComponent = withStyles(styles)(
  withNamespaces()(_PrivacyPolicyUpdateNotificationComponent),
);
